import axios from 'axios';

/**
 * @typedef {('oob' | 'otp' | 'recovery_code')} Auth0MfaAuthenticatorType
 */

/**
 * @typedef {'email' | 'phone' | 'recovery-code' | 'totp' | 'passkey' | 'push'} Auth0ManagementMfaType
 */

/**
 * @typedef {('email' | 'sms' | 'voice' | 'auth0')} Auth0MfaOobChannel
 */

/**
 * @typedef {object} MfaEnrollment
 * @property {string} id
 * @property {string} name
 * @property {Auth0ManagementMfaType} type
 */

/**
 * @typedef {object} StartStepUpAuthRes
 * @property {MfaEnrollment[]} enrollments
 * @property {string} mfaToken
 * @property {boolean} isMfaRequired
 */

/**
 * @param {object} body
 * @param {string} [body.password] - Optional password. If not provided, will try without password first
 * @param {boolean} body.isMfaSettings
 * @param {boolean} [body.tryWithoutPassword] - Whether to attempt passwordless authentication
 * @returns {Promise<StartStepUpAuthRes>}
 */
export const startStepUpAuth = async (body) => {
  const res = await axios.post('../mfa/v2/start-step-up-auth', body);
  return res.data;
};

/**
 * @typedef {object} StartMfaChallengeRes
 * @property {string} mfaToken
 * @property {string} oobCode
 */

/**
 * @param {object} body
 * @param {string} body.mfaToken
 * @param {string} body.authenticatorId
 * @param {Auth0MfaAuthenticatorType} body.challengeType
 * @returns {Promise<StartMfaChallengeRes>}
 */
export const startMfaChallenge = async (body) => {
  const res = await axios.post('../mfa/start-step-up-auth-mfa-challenge', body);
  return res.data;
};

/**
 * https://auth0.com/docs/api/authentication#challenge-request
 * @param {object} body
 * @param {string} [body.bindingCode] user-supplied value when mfa type is 'oob' (sms, email, voice)
 * @param {Auth0MfaAuthenticatorType} body.challengeType
 * @param {string} [body.oobCode] auth0-supplied value to accompany 'oob' challenges
 * @param {string} [body.otpCode] user-supplied value when mfa type is 'otp'
 * @param {string} body.mfaToken auth0-supplied token for the mfa verification session
 * @param {string} [body.userPhone]
 * @param {boolean} body.isMfaSettings
 * @param {{text_hash: string, accepted: boolean}[]} [body.consents] array of hashed consent text and acceptance status for each consent
 * @returns {Promise<{ accessToken: string }>}
 */
export const submitMfaChallenge = async (body) => {
  const res = await axios.post('../mfa/v2/submit-step-up-auth-mfa-challenge', body);
  return res.data;
};
